import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useProject } from '../../contexts/ProjectContext';
import tokenManager from '../../utils/tokenManager';
import { fetchSessions, createSession, fetchSessionMessages, updateSession, deleteSession } from '../../services/chatSessionService';
import { getProjectSummaryStatus, refreshProjectSummary } from '../../services/projectSummaryService';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.releasecadence.dev/api/v1';

const SimpleChat = () => {
  const { activeProject } = useProject();
  const messagesEndRef = React.useRef(null);
  
  const [messages, setMessages] = useState([
    {
      message: "Hello! I'm your AI assistant. I can help you define features, create requirements, and design surveys for your project. What would you like to work on?",
      sentTime: "just now",
      sender: "Assistant",
      direction: "incoming"
    }
  ]);
  const [typingIndicator, setTypingIndicator] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [chatSize, setChatSize] = useState('default'); // 'minimized', 'default', 'wide'
  const [currentSessionId, setCurrentSessionId] = useState(null); // Track active session
  const [sessions, setSessions] = useState([]); // Available sessions
  const [showSessionSelector, setShowSessionSelector] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [showSuggestedPrompts, setShowSuggestedPrompts] = useState(true);
  const [expandedSuggestions, setExpandedSuggestions] = useState({});
  const [selectedRequirements, setSelectedRequirements] = useState({});
  const [creatingFromSuggestion, setCreatingFromSuggestion] = useState(null);
  const [summaryStatus, setSummaryStatus] = useState(null);
  const [refreshingSummary, setRefreshingSummary] = useState(false);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typingIndicator]);

  // Load sessions when the active project ID changes.
  // Deferred by 500 ms so page-level data fetches get DB priority at startup.
  // Depend on activeProject?.id (primitive) not the full object so that a new
  // object reference for the same project does not trigger a redundant reload.
  useEffect(() => {
    if (!activeProject?.id) return;
    const timer = setTimeout(() => {
      loadSessions();
      loadSummaryStatus();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  const loadSessions = async () => {
    if (!activeProject) return;
    
    try {
      const sessionList = await fetchSessions(activeProject.id);
      setSessions(sessionList);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadSummaryStatus = async () => {
    if (!activeProject) return;
    
    try {
      const status = await getProjectSummaryStatus(activeProject.id);
      setSummaryStatus(status);
    } catch (error) {
      console.error('Failed to load summary status:', error);
      setSummaryStatus(null);
    }
  };

  const handleRefreshSummary = async () => {
    if (!activeProject || refreshingSummary) return;
    
    setRefreshingSummary(true);
    try {
      await refreshProjectSummary(activeProject.id);
      await loadSummaryStatus();
      
      const successMsg = {
        message: "✅ **Project summary refreshed!** The AI now has updated context about your project.",
        sentTime: "just now",
        sender: "System",
        direction: "incoming"
      };
      setMessages(prev => [...prev, successMsg]);
    } catch (error) {
      console.error('Failed to refresh summary:', error);
      const errorMsg = {
        message: `❌ **Failed to refresh summary:** ${error.message}. This feature requires a paid plan with AI assistant access.`,
        sentTime: "just now",
        sender: "System",
        direction: "incoming"
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setRefreshingSummary(false);
    }
  };

  const loadSessionMessages = async (sessionId) => {
    try {
      const sessionMessages = await fetchSessionMessages(sessionId);
      
      // Convert session messages to chat format
      const formattedMessages = sessionMessages.map(msg => ({
        message: msg.content,
        sentTime: new Date(msg.created_at).toLocaleTimeString(),
        sender: msg.role === 'user' ? 'User' : 'Assistant',
        direction: msg.role === 'user' ? 'outgoing' : 'incoming',
        suggestions: msg.role === 'assistant' ? extractSuggestionsFromMessage(msg.content) : undefined
      }));
      
      // If no messages, show greeting
      if (formattedMessages.length === 0) {
        formattedMessages.push({
          message: "Hello! I'm your AI assistant. I can help you define features, create requirements, and design surveys for your project. What would you like to work on?",
          sentTime: "just now",
          sender: "Assistant",
          direction: "incoming"
        });
      }
      
      setMessages(formattedMessages);
      setShowSuggestedPrompts(formattedMessages.length === 1);
    } catch (error) {
      console.error('Failed to load session messages:', error);
      // Fallback to greeting message
      setMessages([{
        message: "Hello! I'm your AI assistant. I can help you define features, create requirements, and design surveys for your project. What would you like to work on?",
        sentTime: "just now",
        sender: "Assistant",
        direction: "incoming"
      }]);
    }
  };

  const extractSuggestionsFromMessage = (content) => {
    try {
      const jsonMatch = content.match(/```json\s*({[\s\S]*?})\s*```/);
      if (jsonMatch) {
        const data = JSON.parse(jsonMatch[1]);
        return data.suggestions || null;
      }
    } catch (e) {
      console.error('Failed to extract suggestions:', e);
    }
    return null;
  };

  const handleNewChat = async () => {
    if (!activeProject) return;
    
    try {
      const newSession = await createSession({
        project_id: activeProject.id,
        title: 'New Conversation',
        session_type: 'general'
      });
      
      // Switch to new session and clear messages
      setCurrentSessionId(newSession.id);
      setMessages([{
        message: "Hello! I'm your AI assistant. I can help you define features, create requirements, and design surveys for your project. What would you like to work on?",
        sentTime: "just now",
        sender: "Assistant",
        direction: "incoming"
      }]);
      
      // Show suggested prompts for new conversation
      setShowSuggestedPrompts(true);
      
      // Reload sessions list
      await loadSessions();
      
      // Close session selector
      setShowSessionSelector(false);
    } catch (error) {
      console.error('Failed to create new chat:', error);
    }
  };

  const handleSwitchSession = async (sessionId) => {
    setCurrentSessionId(sessionId);
    await loadSessionMessages(sessionId);
    setShowSessionSelector(false);
  };

  const handleRenameSession = async (sessionId) => {
    if (!editingTitle.trim() || editingTitle === sessions.find(s => s.id === sessionId)?.title) {
      setEditingSessionId(null);
      return;
    }

    try {
      await updateSession(sessionId, { title: editingTitle.trim() });
      await loadSessions();
      setEditingSessionId(null);
    } catch (error) {
      console.error('Failed to rename session:', error);
      alert('Failed to rename conversation. Please try again.');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      return;
    }

    try {
      await deleteSession(sessionId);
      
      // If deleting current session, clear it
      if (sessionId === currentSessionId) {
        setCurrentSessionId(null);
        setMessages([{
          message: "Hello! I'm your AI assistant. I can help you define features, create requirements, and design surveys for your project. What would you like to work on?",
          sentTime: "just now",
          sender: "Assistant",
          direction: "incoming"
        }]);
        setShowSuggestedPrompts(true);
      }
      
      await loadSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const handlePinSession = async (sessionId, isPinned) => {
    try {
      await updateSession(sessionId, { is_pinned: isPinned });
      await loadSessions();
    } catch (error) {
      console.error('Failed to pin session:', error);
      alert('Failed to pin conversation. Please try again.');
    }
  };

  const handleCreateFromSuggestion = async (suggestion, msgIndex) => {
    const suggestionKey = `${msgIndex}-${suggestion.title || suggestion.feature_title}`;
    setCreatingFromSuggestion(suggestionKey);
    
    try {
      const token = tokenManager.getToken();
      if (!token) {
        alert('Please log in to create items');
        setCreatingFromSuggestion(null);
        return;
      }

      if (suggestion.type === 'feature') {
        // Create feature first
        const featureResponse = await fetch('/api/v1/features', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            project_id: activeProject.id,
            title: suggestion.title,
            description: suggestion.description,
            priority: (suggestion.priority || 'MEDIUM').toLowerCase(),
            status: (suggestion.status || 'DRAFT').toLowerCase()
          })
        });

        if (!featureResponse.ok) {
          const errorData = await featureResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || `Failed to create feature (${featureResponse.status})`);
        }

        const feature = await featureResponse.json();
        
        // Get selected requirements for this suggestion
        const suggestionKey = `${msgIndex}-${suggestion.title}`;
        const selected = selectedRequirements[suggestionKey] || {};
        const requirementsToCreate = (suggestion.requirements || []).filter((req, idx) => 
          selected[idx] !== false
        );

        // Create selected requirements under the feature
        let createdCount = 0;
        const failedReqs = [];
        for (const req of requirementsToCreate) {
          try {
            const reqResponse = await fetch(`/api/v1/requirements/features/${feature.id}/requirements`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                title: req.title,
                body: req.body,
                priority: (req.priority || 'SHOULD').toUpperCase(),
                req_type: (req.req_type || 'FUNCTIONAL').toUpperCase()
              })
            });
            if (reqResponse.ok) {
              createdCount++;
            } else {
              failedReqs.push(req.title);
            }
          } catch (err) {
            console.error('Failed to create requirement:', err);
            failedReqs.push(req.title);
          }
        }

        // Show success message with link
        const featureLink = `/projects/${activeProject.id}/features/${feature.id}`;
        let message = `✅ **Feature created successfully!** [${feature.title}](${featureLink}) has been added with ${createdCount} requirement(s).`;
        
        if (failedReqs.length > 0) {
          message += `\n\n⚠️ ${failedReqs.length} requirement(s) failed to create: ${failedReqs.join(', ')}`;
        }
        
        const successMsg = {
          message,
          sentTime: "just now",
          sender: "System",
          direction: "incoming"
        };
        setMessages(prev => [...prev, successMsg]);
        setCreatingFromSuggestion(null);

      } else if (suggestion.type === 'add_requirements') {
        // Add requirements to existing feature
        const suggestionKey = `${msgIndex}-${suggestion.feature_title}`;
        const selected = selectedRequirements[suggestionKey] || {};
        const requirementsToCreate = (suggestion.requirements || []).filter((req, idx) => 
          selected[idx] !== false
        );

        let createdCount = 0;
        const failedReqs = [];
        
        // Use feature-level endpoint if feature_id is provided, otherwise project-level
        const endpoint = suggestion.feature_id 
          ? `/api/v1/requirements/features/${suggestion.feature_id}/requirements`
          : `/api/v1/projects/${activeProject.id}/requirements`;
        
        for (const req of requirementsToCreate) {
          try {
            const reqBody = {
              title: req.title,
              body: req.body,
              priority: (req.priority || 'SHOULD').toUpperCase(),
              req_type: (req.req_type || 'FUNCTIONAL').toUpperCase()
            };
            
            // Only add section for project-level requirements
            if (!suggestion.feature_id) {
              reqBody.section = 'GENERAL';
            }
            
            const reqResponse = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(reqBody)
            });
            if (reqResponse.ok) {
              createdCount++;
            } else {
              failedReqs.push(req.title);
            }
          } catch (err) {
            console.error('Failed to create requirement:', err);
            failedReqs.push(req.title);
          }
        }

        const featureLink = suggestion.feature_id 
          ? `/projects/${activeProject.id}/features/${suggestion.feature_id}`
          : `/projects/${activeProject.id}/requirements`;
        
        let message = `✅ **Requirements added successfully!** ${createdCount} requirement(s) added to [${suggestion.feature_title}](${featureLink}).`;
        
        if (failedReqs.length > 0) {
          message += `\n\n⚠️ ${failedReqs.length} requirement(s) failed to create: ${failedReqs.join(', ')}`;
        }
        
        const successMsg = {
          message,
          sentTime: "just now",
          sender: "System",
          direction: "incoming"
        };
        setMessages(prev => [...prev, successMsg]);
        setCreatingFromSuggestion(null);
      }

    } catch (error) {
      console.error('Failed to create from suggestion:', error);
      const errorMsg = {
        message: `❌ **Failed to create ${suggestion.type}:** ${error.message || 'Unknown error'}. Please try again or create it manually.`,
        sentTime: "just now",
        sender: "System",
        direction: "incoming"
      };
      setMessages(prev => [...prev, errorMsg]);
      setCreatingFromSuggestion(null);
    }
  };

  const toggleRequirementSelection = (suggestionKey, reqIndex) => {
    setSelectedRequirements(prev => ({
      ...prev,
      [suggestionKey]: {
        ...prev[suggestionKey],
        [reqIndex]: prev[suggestionKey]?.[reqIndex] === false ? true : false
      }
    }));
  };

  const toggleSuggestionExpanded = (suggestionKey) => {
    setExpandedSuggestions(prev => ({
      ...prev,
      [suggestionKey]: !prev[suggestionKey]
    }));
  };

  const handleSelectSession = async (sessionId) => {
    await handleSwitchSession(sessionId);
  };

  const autoGenerateSessionTitle = async (sessionId, userMessage, messageCount) => {
    try {
      // Only auto-generate for first user message (messageCount will be 1 after greeting)
      if (messageCount > 1) return;
      
      // Check if current session has default title
      const currentSession = sessions.find(s => s.id === sessionId);
      if (!currentSession || (currentSession.title !== 'New Conversation' && currentSession.title !== 'Untitled')) {
        return;
      }
      
      // Generate concise title from user message (max 50 chars)
      let title = userMessage.trim();
      
      // Extract key intent from message
      const lowerMsg = title.toLowerCase();
      if (lowerMsg.includes('create') || lowerMsg.includes('add') || lowerMsg.includes('new')) {
        // Extract what they want to create
        const match = title.match(/(?:create|add|new)\s+(?:a\s+)?(?:feature|requirement|survey)?\s*(?:for|about)?\s*(.+)/i);
        if (match && match[1]) {
          title = match[1].trim();
        }
      }
      
      // Truncate and clean up
      title = title
        .replace(/^(please|can you|could you|i want to|i need to|help me)\s+/i, '')
        .replace(/[?!.]+$/, '')
        .substring(0, 50);
      
      if (title.length === 50) {
        title = title.substring(0, 47) + '...';
      }
      
      // Capitalize first letter
      title = title.charAt(0).toUpperCase() + title.slice(1);
      
      // Update session title
      await updateSession(sessionId, { title });
      await loadSessions();
    } catch (error) {
      console.error('Failed to auto-generate session title:', error);
      // Don't show error to user - this is a nice-to-have feature
    }
  };

  const handleSend = async (innerHtml, textContent, innerText) => {
    if (!textContent.trim()) return;

    // Check if user has a project selected
    if (!activeProject) {
      const errorMsg = {
        message: "Please select a project first to use the AI assistant.",
        sentTime: "just now",
        sender: "System",
        direction: "incoming"
      };
      setMessages(prev => [...prev, errorMsg]);
      return;
    }

    // Add user message
    const userMessage = {
      message: textContent,
      sentTime: "just now",
      sender: "User",
      direction: "outgoing"
    };

    setMessages(prev => [...prev, userMessage]);
    setTypingIndicator(true);

    try {
      const token = tokenManager.getToken();
      if (!token) {
        const errorMsg = {
          message: "Authentication required. Please log in again.",
          sentTime: "just now",
          sender: "System",
          direction: "incoming"
        };
        setMessages(prev => [...prev, errorMsg]);
        setTypingIndicator(false);
        return;
      }

      const response = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project_id: activeProject.id,
          message: textContent,
          session_id: currentSessionId // Include current session if exists
        })
      });

      if (response.status === 402) {
        // Free plan user - show upgrade prompt
        const error = await response.json();
        const errorMsg = {
          message: error.detail?.message || "AI assistant requires a paid plan. Please upgrade to access this feature.",
          sentTime: "just now",
          sender: "System",
          direction: "incoming"
        };
        setMessages(prev => [...prev, errorMsg]);
        setTypingIndicator(false);
        return;
      }

      if (response.status === 429) {
        // Quota exceeded
        const error = await response.json();
        const errorMsg = {
          message: `You've reached your AI message limit. Your quota resets on ${error.detail?.reset_date || 'next month'}.`,
          sentTime: "just now",
          sender: "System",
          direction: "incoming"
        };
        setMessages(prev => [...prev, errorMsg]);
        setTypingIndicator(false);
        return;
      }

      if (!response.ok) {
        throw new Error('API error');
      }

      const data = await response.json();
      
      // Parse usage headers for warnings
      const usageWarning = response.headers.get('X-AI-Warning');
      const warningSeverity = response.headers.get('X-AI-Warning-Severity');
      
      // Update current session ID from response
      const sessionId = data.session_id || currentSessionId;
      if (data.session_id && !currentSessionId) {
        setCurrentSessionId(data.session_id);
      }
      
      // Auto-generate title for new conversations
      if (sessionId) {
        await autoGenerateSessionTitle(sessionId, textContent, messages.length);
      }
      
            
      const assistantMessage = {
        message: data.message,
        sentTime: "just now",
        sender: "Assistant",
        direction: "incoming",
        suggestions: data.suggestions || []
      };

      const newMessages = [assistantMessage];
      
      // Add usage warning if present
      if (usageWarning && warningSeverity) {
        const warningIcon = warningSeverity === 'critical' ? '🚨' : warningSeverity === 'warning' ? '⚠️' : 'ℹ️';
        const warningMessage = {
          message: `${warningIcon} **Usage Alert:** ${usageWarning}`,
          sentTime: "just now",
          sender: "System",
          direction: "incoming",
          severity: warningSeverity
        };
        newMessages.push(warningMessage);
      }

      setMessages(prev => [...prev, ...newMessages]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMsg = {
        message: "Sorry, I'm having trouble connecting. Please try again.",
        sentTime: "just now",
        sender: "System",
        direction: "incoming"
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setTypingIndicator(false);
    }
  };

  // Size configurations
  const sizeConfig = {
    minimized: { width: 0, height: 0, display: 'none' },
    default: { width: '350px', height: '500px' },
    wide: { width: '600px', height: '600px' }
  };

  const currentSize = sizeConfig[chatSize];

  if (isMinimized) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <button
          onClick={() => setIsMinimized(false)}
          style={{
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          💬
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      width: currentSize.width,
      height: currentSize.height,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transition: 'width 0.3s ease, height 0.3s ease'
    }}>
      <div style={{
        backgroundColor: '#1976d2',
        color: 'white',
        padding: '10px',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
          <span>Chat Assistant</span>
          {currentSessionId && (
            <span style={{ fontSize: '11px', opacity: 0.8 }}>• Active</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Refresh Summary button — only shown when a newer checkpoint exists */}
          {summaryStatus?.needs_refresh && (
            <button
              onClick={handleRefreshSummary}
              disabled={refreshingSummary}
              style={{
                backgroundColor: 'rgba(255,193,7,0.3)',
                color: 'white',
                border: '1px solid white',
                borderRadius: '4px',
                fontSize: '11px',
                cursor: refreshingSummary ? 'wait' : 'pointer',
                padding: '2px 6px',
                opacity: refreshingSummary ? 0.6 : 1
              }}
              title="New checkpoint available — refresh AI context"
            >
              {refreshingSummary ? '⟳...' : '⟳ AI'}
            </button>
          )}
          {/* New Chat button */}
          <button
            onClick={handleNewChat}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid white',
              borderRadius: '4px',
              fontSize: '11px',
              cursor: 'pointer',
              padding: '2px 6px'
            }}
            title="Start new conversation"
          >
            + New
          </button>
          {/* Session selector */}
          {sessions.length > 0 && (
            <button
              onClick={() => setShowSessionSelector(!showSessionSelector)}
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                border: '1px solid white',
                borderRadius: '4px',
                fontSize: '11px',
                cursor: 'pointer',
                padding: '2px 6px'
              }}
              title="Switch conversation"
            >
              ☰
            </button>
          )}
          {/* Size toggle buttons */}
          <button
            onClick={() => setChatSize(chatSize === 'default' ? 'wide' : 'default')}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid white',
              borderRadius: '4px',
              fontSize: '11px',
              cursor: 'pointer',
              padding: '2px 6px'
            }}
            title={chatSize === 'default' ? 'Expand' : 'Default size'}
          >
            {chatSize === 'default' ? '⇔' : '⇄'}
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '0',
              width: '20px',
              height: '20px'
            }}
          >
            −
          </button>
        </div>
      </div>
      
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'white'
      }}>
        {/* Session selector dropdown */}
        {showSessionSelector && sessions.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '50px',
            right: '10px',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1001,
            maxHeight: '300px',
            overflowY: 'auto',
            minWidth: '250px'
          }}>
            <div style={{ padding: '8px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '12px' }}>
              Your Conversations
            </div>
            {sessions
              .sort((a, b) => {
                // Pinned sessions first
                if (a.is_pinned && !b.is_pinned) return -1;
                if (!a.is_pinned && b.is_pinned) return 1;
                // Then by last message time
                return new Date(b.last_message_at) - new Date(a.last_message_at);
              })
              .map(session => (
              <div
                key={session.id}
                style={{
                  padding: '10px',
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: session.id === currentSessionId ? '#f0f7ff' : 'white',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {session.is_pinned && (
                  <span style={{ fontSize: '14px' }} title="Pinned">📌</span>
                )}
                <div 
                  onClick={() => handleSelectSession(session.id)}
                  style={{ flex: 1, cursor: 'pointer' }}
                >
                  {editingSessionId === session.id ? (
                    <input
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => handleRenameSession(session.id)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') handleRenameSession(session.id);
                        if (e.key === 'Escape') setEditingSessionId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '2px 4px',
                        fontSize: '13px',
                        border: '1px solid #1976d2',
                        borderRadius: '2px'
                      }}
                    />
                  ) : (
                    <div style={{ fontWeight: session.id === currentSessionId ? 'bold' : 'normal' }}>
                      {session.title}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    {session.message_count} messages • {session.session_type}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePinSession(session.id, !session.is_pinned);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '2px 4px'
                    }}
                    title={session.is_pinned ? 'Unpin' : 'Pin'}
                  >
                    {session.is_pinned ? '📍' : '📌'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingSessionId(session.id);
                      setEditingTitle(session.title);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '2px 4px'
                    }}
                    title="Rename"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSession(session.id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '2px 4px',
                      color: '#d32f2f'
                    }}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px',
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {messages.map((msg, index) => (
            <div key={index} style={{ marginBottom: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: msg.direction === 'outgoing' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start'
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    backgroundColor: msg.direction === 'outgoing' ? '#1976d2' : '#f1f3f4',
                    color: msg.direction === 'outgoing' ? 'white' : '#333',
                    wordWrap: 'break-word'
                  }}
                >
                  <div style={{ 
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {msg.sender === 'Assistant' || msg.sender === 'System' ? (
                      <ReactMarkdown
                        components={{
                          code: ({node, inline, className, children, ...props}) => {
                            // Hide JSON suggestion blocks from display
                            const code = String(children).replace(/\n$/, '');
                            if (!inline && code.includes('"suggestions"')) {
                              return null;
                            }
                            return inline ? 
                              <code style={{ 
                                backgroundColor: msg.direction === 'outgoing' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '13px'
                              }} {...props}>{children}</code> :
                              <code style={{
                                display: 'block',
                                backgroundColor: msg.direction === 'outgoing' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                padding: '8px',
                                borderRadius: '4px',
                                fontSize: '13px',
                                overflowX: 'auto',
                                whiteSpace: 'pre-wrap'
                              }} {...props}>{children}</code>;
                          },
                          p: ({node, ...props}) => <p style={{ margin: '0 0 8px 0' }} {...props} />,
                          ul: ({node, ...props}) => <ul style={{ margin: '0 0 8px 0', paddingLeft: '20px' }} {...props} />,
                          ol: ({node, ...props}) => <ol style={{ margin: '0 0 8px 0', paddingLeft: '20px' }} {...props} />,
                          li: ({node, ...props}) => <li style={{ margin: '4px 0' }} {...props} />,
                          strong: ({node, ...props}) => <strong style={{ fontWeight: 600 }} {...props} />,
                          em: ({node, ...props}) => <em style={{ fontStyle: 'italic' }} {...props} />
                        }}
                      >
                        {msg.message}
                      </ReactMarkdown>
                    ) : (
                      msg.message
                    )}
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    opacity: 0.7, 
                    marginTop: '4px' 
                  }}>
                    {msg.sentTime}
                  </div>
                </div>
              </div>
              
              {/* AI Suggestions - Grouped features with requirements */}
              {msg.suggestions && msg.suggestions.length > 0 && (
                <div style={{
                  marginTop: '8px',
                  width: '100%'
                }}>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
                      💡 AI Suggestions - Review and create:
                    </div>
                    {msg.suggestions.map((suggestion, idx) => {
                      const suggestionKey = `${index}-${suggestion.title || suggestion.feature_title}`;
                      const isExpanded = expandedSuggestions[suggestionKey];
                      const hasRequirements = suggestion.requirements && suggestion.requirements.length > 0;
                      
                      return (
                        <div key={idx} style={{
                          marginBottom: idx < msg.suggestions.length - 1 ? '8px' : '0',
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0'
                        }}>
                          {/* Feature Header */}
                          <div style={{ marginBottom: hasRequirements ? '8px' : '12px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                              {suggestion.type === 'feature' ? '🎯' : '📋'} {suggestion.title || suggestion.feature_title}
                            </div>
                            {suggestion.description && (
                              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                {suggestion.description}
                              </div>
                            )}
                            <div style={{ fontSize: '11px', color: '#666' }}>
                              {suggestion.type === 'feature' && `Priority: ${suggestion.priority}`}
                              {suggestion.type === 'add_requirements' && 'Adding to existing feature'}
                            </div>
                          </div>

                          {/* Requirements Section */}
                          {hasRequirements && (
                            <div style={{
                              marginBottom: '12px',
                              padding: '8px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0'
                            }}>
                              <div 
                                onClick={() => toggleSuggestionExpanded(suggestionKey)}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  marginBottom: isExpanded ? '8px' : '0'
                                }}
                              >
                                <span style={{ fontSize: '12px', fontWeight: '600', color: '#555' }}>
                                  📋 {suggestion.requirements.length} Requirement{suggestion.requirements.length > 1 ? 's' : ''}
                                </span>
                                <span style={{ fontSize: '12px', color: '#666' }}>
                                  {isExpanded ? '▼' : '▶'} {isExpanded ? 'Hide' : 'Show'}
                                </span>
                              </div>

                              {isExpanded && (
                                <div style={{ paddingLeft: '8px' }}>
                                  {suggestion.requirements.map((req, reqIdx) => {
                                    const isSelected = selectedRequirements[suggestionKey]?.[reqIdx] !== false;
                                    return (
                                      <div key={reqIdx} style={{
                                        marginBottom: reqIdx < suggestion.requirements.length - 1 ? '6px' : '0',
                                        padding: '6px',
                                        backgroundColor: 'white',
                                        borderRadius: '3px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px'
                                      }}>
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleRequirementSelection(suggestionKey, reqIdx)}
                                          style={{
                                            marginTop: '2px',
                                            cursor: 'pointer',
                                            width: '16px',
                                            height: '16px',
                                            flexShrink: 0
                                          }}
                                        />
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontSize: '12px', fontWeight: '500', color: '#333' }}>
                                            {req.title}
                                          </div>
                                          <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                            {req.req_type} - {req.priority}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Create Button */}
                          <button
                            onClick={() => handleCreateFromSuggestion(suggestion, index)}
                            disabled={creatingFromSuggestion === suggestionKey}
                            style={{
                              width: '100%',
                              padding: '10px 12px',
                              backgroundColor: creatingFromSuggestion === suggestionKey ? '#999' : '#1976d2',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: creatingFromSuggestion === suggestionKey ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                              opacity: creatingFromSuggestion === suggestionKey ? 0.7 : 1
                            }}
                          >
                            {creatingFromSuggestion === suggestionKey ? (
                              <>⏳ Creating...</>
                            ) : (
                              suggestion.type === 'feature' 
                                ? `Create Feature${hasRequirements ? ' & Requirements' : ''}` 
                                : 'Add Requirements'
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {typingIndicator && (
            <div style={{
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'flex-start'
            }}>
              <div style={{
                maxWidth: '85%',
                padding: '12px 16px',
                borderRadius: '12px',
                backgroundColor: '#f1f3f4',
                display: 'flex',
                gap: '4px',
                alignItems: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#666',
                    animation: 'bounce 1.4s infinite ease-in-out both',
                    animationDelay: '0s'
                  }} />
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#666',
                    animation: 'bounce 1.4s infinite ease-in-out both',
                    animationDelay: '0.2s'
                  }} />
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#666',
                    animation: 'bounce 1.4s infinite ease-in-out both',
                    animationDelay: '0.4s'
                  }} />
                </div>
              </div>
              <style>{`
                @keyframes bounce {
                  0%, 80%, 100% {
                    transform: scale(0);
                    opacity: 0.5;
                  }
                  40% {
                    transform: scale(1);
                    opacity: 1;
                  }
                }
              `}</style>
            </div>
          )}
          <div ref={messagesEndRef} />
          
          {/* Suggested prompts - show when no messages yet */}
          {messages.length === 1 && showSuggestedPrompts && (
            <div style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                Quick Actions:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    const input = document.querySelector('textarea');
                    input.value = 'Help me define a new feature for this project';
                    input.focus();
                    setShowSuggestedPrompts(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f0f7ff',
                    border: '1px solid #1976d2',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#1976d2'
                  }}
                >
                  💡 Define a new feature
                </button>
                <button
                  onClick={() => {
                    const input = document.querySelector('textarea');
                    input.value = 'Create requirements for user authentication';
                    input.focus();
                    setShowSuggestedPrompts(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f0f7ff',
                    border: '1px solid #1976d2',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#1976d2'
                  }}
                >
                  📋 Create requirements
                </button>
                <button
                  onClick={() => {
                    const input = document.querySelector('textarea');
                    input.value = 'Review my existing features and suggest improvements';
                    input.focus();
                    setShowSuggestedPrompts(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f0f7ff',
                    border: '1px solid #1976d2',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#1976d2'
                  }}
                >
                  🔍 Review features
                </button>
                <button
                  onClick={() => {
                    const input = document.querySelector('textarea');
                    input.value = 'Help me break down a complex feature into smaller requirements';
                    input.focus();
                    setShowSuggestedPrompts(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f0f7ff',
                    border: '1px solid #1976d2',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#1976d2'
                  }}
                >
                  🎯 Break down feature
                </button>
                <button
                  onClick={() => {
                    const input = document.querySelector('textarea');
                    input.value = 'Design a customer survey for feedback';
                    input.focus();
                    setShowSuggestedPrompts(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f0f7ff',
                    border: '1px solid #1976d2',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#1976d2'
                  }}
                >
                  📊 Design survey
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
          padding: '10px',
          borderTop: '1px solid #eee',
          backgroundColor: 'white',
          flexShrink: 0
        }}>
          <textarea
            placeholder="Type message here (press Enter to send, Shift+Enter for new line)"
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && e.target.value.trim()) {
                e.preventDefault();
                handleSend(null, e.target.value, e.target.value);
                e.target.value = '';
                e.target.style.height = 'auto';
              }
            }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            style={{
              flex: 1,
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
              resize: 'none',
              minHeight: '38px',
              maxHeight: '120px',
              fontFamily: 'inherit',
              lineHeight: '1.5'
            }}
          />
          <button
            onClick={() => {
              const input = document.querySelector('textarea');
              if (input && input.value.trim()) {
                handleSend(null, input.value, input.value);
                input.value = '';
                input.style.height = 'auto';
              }
            }}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleChat;
